// The archived-items review page (spec 2026-10-08-02): hooks over
// /api/v1/organizations/{orgCode}/archived-items and the document
// archive/restore actions. The page needs `archive.read`, deleting `archive.write`.
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import type { components } from '../api/schema'

export type ArchivedAsset = components['schemas']['ArchivedAsset']
export type ArchivedDocument = components['schemas']['ArchivedDocument']
export type ArchiveDeletePreview = components['schemas']['ArchiveDeletePreview']
export type ArchiveDeleteBlocker = components['schemas']['ArchiveDeleteBlocker']
export type ArchivedKind = 'assets' | 'documents'
export type ArchiveBatchFilter = components['schemas']['ArchiveBatchFilter']
export type ArchiveBatchPreview = components['schemas']['ArchiveBatchPreview']
export type ArchiveBatchResult = NonNullable<components['schemas']['ArchiveBulkDeleteResponse']['results']>[number]

/** The server caps one batch delete call at 200 roots; the page sends bigger selections in chunks. */
export const BATCH_CHUNK = 200

/** The stale threshold setting the age filter is prefilled with. */
export const STALE_SETTING_CODE = 'archive.stale_after_days'

export interface ArchivedListParams {
    kind: ArchivedKind
    q?: string
    type?: string
    sort?: 'archived_at' | '-archived_at'
    /** RFC 3339 instant: keep what was archived before. */
    archivedBefore?: string
    /** Keep what was archived longer ago than the stale threshold. */
    stale?: boolean
    page?: number
    limit?: number
}

const ARCHIVED_ITEMS = '/api/v1/organizations/{orgCode}/archived-items'

/** invalidateArchive refreshes the archive lists and every asset or document list. */
function invalidateArchive(queryClient: QueryClient) {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const [method, path] = query.queryKey
            return (
                method === 'get' &&
                typeof path === 'string' &&
                (path.startsWith(ARCHIVED_ITEMS) || path.endsWith('/assets') || path.includes('/documents'))
            )
        },
    })
}

/** useInvalidateArchive returns a refresh of the archive lists, for actions owned elsewhere (asset restore). */
export function useInvalidateArchive() {
    const queryClient = useQueryClient()
    return () => invalidateArchive(queryClient)
}

export function useArchivedItems(orgCode: string, params: ArchivedListParams) {
    return $api.useQuery(
        'get',
        ARCHIVED_ITEMS,
        {
            params: {
                path: { orgCode },
                query: {
                    kind: params.kind,
                    q: params.q || undefined,
                    type: params.type || undefined,
                    sort: params.sort,
                    archived_before: params.archivedBefore || undefined,
                    stale: params.stale || undefined,
                    page: params.page,
                    limit: params.limit,
                },
            },
        },
        { enabled: !!orgCode }
    )
}

export function useArchiveDeletePreview(orgCode: string, assetUid: string | undefined) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/archived-items/assets/{assetUid}/delete-preview',
        { params: { path: { orgCode, assetUid: assetUid ?? '' } } },
        { enabled: !!orgCode && !!assetUid, staleTime: 0, gcTime: 0 }
    )
}

export function useDeleteArchivedAsset(orgCode: string) {
    const queryClient = useQueryClient()
    const mutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/archived-items/assets/{assetUid}', {
        onSuccess: () => invalidateArchive(queryClient),
    })
    return {
        ...mutation,
        mutateAsync: (vars: { assetUid: string; confirm: string; expectedDescendantCount: number }) =>
            mutation.mutateAsync({
                params: {
                    path: { orgCode, assetUid: vars.assetUid },
                    query: { confirm: vars.confirm, expected_descendant_count: vars.expectedDescendantCount },
                },
            }),
    }
}

export function useBulkDeleteArchived(orgCode: string) {
    const queryClient = useQueryClient()
    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/archived-items/delete', {
        onSuccess: () => invalidateArchive(queryClient),
    })
    return {
        ...mutation,
        mutateAsync: (items: { asset_uid: string; confirm: string; expected_descendant_count: number }[]) =>
            mutation.mutateAsync({ params: { path: { orgCode } }, body: { items } }),
    }
}

/** useArchiveBatchPreview resolves a batch (uids or filter) to its summed delete impact. */
export function useArchiveBatchPreview(orgCode: string) {
    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/archived-items/delete-preview')
    return {
        ...mutation,
        mutateAsync: (body: { kind: ArchivedKind; uids?: string[]; filter?: ArchiveBatchFilter }) =>
            mutation.mutateAsync({ params: { path: { orgCode } }, body }),
    }
}

/** useBatchDeleteArchived deletes one chunk (200 at most) of previewed uids. */
export function useBatchDeleteArchived(orgCode: string) {
    const queryClient = useQueryClient()
    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/archived-items/delete', {
        onSuccess: () => invalidateArchive(queryClient),
    })
    return {
        ...mutation,
        mutateAsync: (body: { kind: ArchivedKind; uids: string[]; expected_count: number }) =>
            mutation.mutateAsync({ params: { path: { orgCode } }, body }),
    }
}

/** useSetStaleAfterDays writes the organization setting that defines "stale". */
export function useSetStaleAfterDays(orgCode: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (days: number) => {
            const response = await (
                apiClient as unknown as {
                    POST: (
                        path: string,
                        opts: { params: { path: { orgCode: string; code: string } }; body: { value: unknown } }
                    ) => Promise<{ data?: unknown; error?: unknown }>
                }
            ).POST('/api/v1/organizations/{orgCode}/settings/{code}', {
                params: { path: { orgCode, code: STALE_SETTING_CODE } },
                body: { value: days },
            })
            if (response.error) throw response.error
            return response.data
        },
        onSuccess: () => invalidateArchive(queryClient),
    })
}

export function useArchiveDocument(orgCode: string) {
    const queryClient = useQueryClient()
    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/documents/{documentUid}/archive', {
        onSuccess: () => invalidateArchive(queryClient),
    })
    return {
        ...mutation,
        mutateAsync: (documentUid: string) => mutation.mutateAsync({ params: { path: { orgCode, documentUid } } }),
    }
}

export function useRestoreDocument(orgCode: string) {
    const queryClient = useQueryClient()
    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/documents/{documentUid}/restore', {
        onSuccess: () => invalidateArchive(queryClient),
    })
    return {
        ...mutation,
        mutateAsync: (documentUid: string) => mutation.mutateAsync({ params: { path: { orgCode, documentUid } } }),
    }
}

/** typeLabel picks a type name in the UI language, falling back to its code. */
export function typeLabel(name: Record<string, string> | undefined, code: string | undefined, language: string): string {
    if (name) return name[language.slice(0, 2)] ?? name.en ?? code ?? ''
    return code ?? ''
}

/** confirmMatches mirrors the server rule: the name (case-insensitive) or an 8+ character uid prefix. */
export function confirmMatches(typed: string, name: string | undefined, uid: string): boolean {
    const value = typed.trim()
    if (!value) return false
    if (name && name.trim().toLowerCase() === value.toLowerCase()) return true
    return value.length >= 8 && uid.startsWith(value.toLowerCase())
}
